import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../permissions/permission.service';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { PagesService } from '../../../shared/services/pages.service';
import { MediaService, type MediaAssetDto } from '../../../shared/services/media.service';
import { CountriesService, type CountryDto } from '../../../shared/services/countries.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminPageHeaderComponent,
  AdminSkeletonComponent,
  FloatingActionBarComponent,
} from '../../../shared/admin';

export type HeroGalleryFormEntry = {
  key: string;
  mediaAssetId: string;
  caption: string;
};

/** Editable snapshot of platform home marketing document (flat + nested editor fields). */
export type HomeMarketingForm = {
  announcementPill: string;
  announcementText: string;
  announcementCtaLabel: string;
  announcementCtaHref: string;
  popupEnabled: boolean;
  popupDelaySeconds: number;
  popupImageMediaAssetId: string;
  socialInstagram: string;
  socialFacebook: string;
  socialYoutube: string;
  heroBadge: string;
  heroTitle: string;
  heroLede: string;
  heroFeaturesText: string;
  heroPrimaryCtaText: string;
  heroPrimaryCtaAnchor: string;
  heroMediaAssetId: string;
  heroArtMediaAssetId: string;
  heroGallery: HeroGalleryFormEntry[];
  heroGalleryCoverMediaAssetId: string;
  heroSideBadgeValue: string;
  heroSideBadgeLabel: string;
  statsText: string;
  featuredCountrySlugs: string[];
  featuredCountryCardMedia: Record<string, string>;
  destinationsEyebrow: string;
  destinationsTitle: string;
  destinationsSubtitle: string;
  whyEyebrow: string;
  whyTitle: string;
  whySubtitle: string;
  whyFeaturesJson: string;
  testimonialIdsCsv: string;
  testimonialsTitle: string;
  testimonialsSubtitle: string;
  partnersTitle: string;
  partnersJson: string;
  ctaEyebrow: string;
  ctaTitle: string;
  ctaSubtitle: string;
  ctaBulletsCsv: string;
  ctaFormHeading: string;
  ctaLegalLine: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywordsCsv: string;
  seoOgImageMediaAssetId: string;
};

@Component({
  selector: 'app-home-marketing-page',
  standalone: true,
  imports: [
    DragDropModule,
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminCardComponent,
    AdminSkeletonComponent,
    FloatingActionBarComponent,
  ],
  templateUrl: './home-marketing-page.component.html',
})
export class HomeMarketingPageComponent {
  private readonly pages = inject(PagesService);
  private readonly media = inject(MediaService);
  private readonly countries = inject(CountriesService);
  private readonly permissions = inject(PermissionService);

  readonly showEdit = computed(() => this.permissions.canShow('website.home', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.home', 'edit'));
  readonly showUpload = computed(() => this.permissions.canShow('website.home', 'upload'));
  readonly canUpload = computed(() => this.permissions.canExecute('website.home', 'upload'));

  readonly maxFeaturedCountries = 4;

  readonly loading = signal(false);
  readonly countriesLoading = signal(false);
  readonly countriesList = signal<CountryDto[]>([]);
  readonly featuredCountriesMessage = signal<string | null>(null);
  readonly countryCardPreviewUrls = signal<Record<string, string>>({});
  readonly countryCardUploadingSlug = signal<string | null>(null);
  readonly countryCardUploadMessage = signal<string | null>(null);

  private countryCardFiles: Record<string, File | null> = {};
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly loadError = signal<string | null>(null);
  readonly heroBgUploading = signal(false);
  readonly heroBgPreviewUrl = signal<string | null>(null);
  readonly heroBgUploadMessage = signal<string | null>(null);
  readonly heroGalleryUploading = signal(false);
  readonly heroGalleryPreviewUrls = signal<Record<string, string>>({});
  readonly heroGalleryMessage = signal<string | null>(null);
  readonly popupUploading = signal(false);
  readonly popupPreviewUrl = signal<string | null>(null);
  readonly popupUploadMessage = signal<string | null>(null);

  form: HomeMarketingForm = this.emptyForm();
  heroBgFile: File | null = null;
  popupFile: File | null = null;
  readonly maxHeroGalleryImages = 24;
  private heroGalleryKeySeq = 0;

  constructor() {
    this.load();
    this.loadCountries();
  }

  emptyForm(): HomeMarketingForm {
    return {
      announcementPill: '',
      announcementText: '',
      announcementCtaLabel: '',
      announcementCtaHref: '',
      popupEnabled: false,
      popupDelaySeconds: 5,
      popupImageMediaAssetId: '',
      socialInstagram: '',
      socialFacebook: '',
      socialYoutube: '',
      heroBadge: '',
      heroTitle: '',
      heroLede: '',
      heroFeaturesText: '',
      heroPrimaryCtaText: '',
      heroPrimaryCtaAnchor: '',
      heroMediaAssetId: '',
      heroArtMediaAssetId: '',
      heroGallery: [],
      heroGalleryCoverMediaAssetId: '',
      heroSideBadgeValue: '',
      heroSideBadgeLabel: '',
      statsText: '',
      featuredCountrySlugs: [],
      featuredCountryCardMedia: {},
      destinationsEyebrow: '',
      destinationsTitle: '',
      destinationsSubtitle: '',
      whyEyebrow: '',
      whyTitle: '',
      whySubtitle: '',
      whyFeaturesJson: '[]',
      testimonialIdsCsv: '',
      testimonialsTitle: '',
      testimonialsSubtitle: '',
      partnersTitle: '',
      partnersJson: '[]',
      ctaEyebrow: '',
      ctaTitle: '',
      ctaSubtitle: '',
      ctaBulletsCsv: '',
      ctaFormHeading: '',
      ctaLegalLine: '',
      seoTitle: '',
      seoDescription: '',
      seoKeywordsCsv: '',
      seoOgImageMediaAssetId: '',
    };
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.pages.getPlatformMarketingHome().subscribe({
      next: (d) => {
        this.form = this.docToForm(d);
        this.loadHeroBackgroundPreview(this.form.heroMediaAssetId);
        this.loadHeroGalleryPreviews();
        this.loadPopupPreview(this.form.popupImageMediaAssetId);
        this.loadCountryCardPreviews();
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set('Could not load home marketing content. You can still save to create the singleton document.');
        this.form = this.emptyForm();
        this.heroBgPreviewUrl.set(null);
        this.heroGalleryPreviewUrls.set({});
        this.popupPreviewUrl.set(null);
      },
    });
  }

  loadCountries(): void {
    this.countriesLoading.set(true);
    this.countries.listPlatformCountries().subscribe({
      next: (list) => {
        this.countriesList.set([...list].sort((a, b) => a.name.localeCompare(b.name)));
        this.countriesLoading.set(false);
      },
      error: () => this.countriesLoading.set(false),
    });
  }

  /** Published countries plus any already-selected drafts (so they can be unchecked). */
  countriesForPicker(): CountryDto[] {
    const all = this.countriesList();
    const selected = new Set(this.form.featuredCountrySlugs);
    const published = all.filter((c) => c.status === 'PUBLISHED');
    const selectedDrafts = all.filter((c) => selected.has(c.slug) && c.status !== 'PUBLISHED');
    const slugs = new Set<string>();
    const merged: CountryDto[] = [];
    for (const c of [...selectedDrafts, ...published]) {
      if (slugs.has(c.slug)) continue;
      slugs.add(c.slug);
      merged.push(c);
    }
    return merged;
  }

  featuredCountriesCount(): number {
    return this.form.featuredCountrySlugs.length;
  }

  isFeaturedCountry(slug: string): boolean {
    return this.form.featuredCountrySlugs.includes(slug);
  }

  canToggleFeaturedCountry(country: CountryDto): boolean {
    if (this.isFeaturedCountry(country.slug)) return true;
    if (country.status !== 'PUBLISHED') return false;
    return this.form.featuredCountrySlugs.length < this.maxFeaturedCountries;
  }

  toggleFeaturedCountry(slug: string, checked: boolean): void {
    this.featuredCountriesMessage.set(null);
    const current = [...this.form.featuredCountrySlugs];

    if (checked) {
      if (current.includes(slug)) return;
      if (current.length >= this.maxFeaturedCountries) {
        this.featuredCountriesMessage.set(`You can feature at most ${this.maxFeaturedCountries} countries.`);
        return;
      }
      this.form.featuredCountrySlugs = [...current, slug];
      return;
    }

    this.form.featuredCountrySlugs = current.filter((s) => s !== slug);
    const { [slug]: _removed, ...rest } = this.form.featuredCountryCardMedia;
    this.form.featuredCountryCardMedia = rest;
    this.countryCardFiles[slug] = null;
    this.countryCardPreviewUrls.update((prev) => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  }

  selectedFeaturedCountries(): CountryDto[] {
    const bySlug = new Map(this.countriesList().map((c) => [c.slug, c]));
    return this.form.featuredCountrySlugs
      .map((slug) => bySlug.get(slug))
      .filter((c): c is CountryDto => !!c);
  }

  countryCardPreview(slug: string): string | null {
    return this.countryCardPreviewUrls()[slug] ?? null;
  }

  hasCountryCardFile(slug: string): boolean {
    return !!this.countryCardFiles[slug];
  }

  onCountryCardFile(slug: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.countryCardFiles[slug] = input.files?.[0] ?? null;
    this.countryCardUploadMessage.set(null);
  }

  uploadCountryCard(slug: string): void {
    const file = this.countryCardFiles[slug];
    if (!file) return;
    const country = this.countriesList().find((c) => c.slug === slug);
    this.countryCardUploadingSlug.set(slug);
    this.countryCardUploadMessage.set(null);
    this.media.upload(file, { altText: `${country?.name ?? slug} destination card` }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          this.countryCardUploadingSlug.set(null);
          this.countryCardUploadMessage.set('Upload succeeded but no asset id was returned.');
          return;
        }
        this.form.featuredCountryCardMedia = { ...this.form.featuredCountryCardMedia, [slug]: assetId };
        const previewUrl = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
        this.countryCardPreviewUrls.update((prev) => ({ ...prev, [slug]: previewUrl }));
        this.countryCardFiles[slug] = null;
        this.persistFeaturedCountries(() => {
          this.countryCardUploadingSlug.set(null);
          this.countryCardUploadMessage.set(`Card image saved for ${country?.name ?? slug}.`);
        }, (msg) => {
          this.countryCardUploadingSlug.set(null);
          this.countryCardUploadMessage.set(msg);
        });
      },
      error: (err) => {
        this.countryCardUploadingSlug.set(null);
        this.countryCardUploadMessage.set(err?.error?.message ?? 'Upload failed.');
      },
    });
  }

  clearCountryCard(slug: string): void {
    const { [slug]: _removed, ...rest } = this.form.featuredCountryCardMedia;
    this.form.featuredCountryCardMedia = rest;
    this.countryCardFiles[slug] = null;
    this.countryCardPreviewUrls.update((prev) => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
    this.persistFeaturedCountries(
      () => this.countryCardUploadMessage.set('Card image removed.'),
      (msg) => this.countryCardUploadMessage.set(msg),
    );
  }

  private buildFeaturedCountriesPayload(): {
    featuredCountries: Array<{ slug: string; cardMediaAssetId?: string }>;
    featuredCountrySlugs: string[];
    featuredCountryCardMedia: Record<string, string>;
  } {
    const featuredCountries = this.form.featuredCountrySlugs.slice(0, this.maxFeaturedCountries).map((slug) => {
      const cardMediaAssetId = this.form.featuredCountryCardMedia[slug]?.trim();
      return { slug, ...(cardMediaAssetId ? { cardMediaAssetId } : {}) };
    });
    const featuredCountryCardMedia: Record<string, string> = {};
    for (const entry of featuredCountries) {
      if (entry.cardMediaAssetId) featuredCountryCardMedia[entry.slug] = entry.cardMediaAssetId;
    }
    return {
      featuredCountries,
      featuredCountrySlugs: featuredCountries.map((e) => e.slug),
      featuredCountryCardMedia,
    };
  }

  private persistFeaturedCountries(onSuccess: () => void, onError: (message: string) => void): void {
    this.pages.patchPlatformMarketingHome(this.buildFeaturedCountriesPayload()).subscribe({
      next: (d) => {
        this.applyFeaturedCountriesFromDoc(d);
        onSuccess();
      },
      error: (err) => onError(err?.error?.message ?? 'Could not save featured countries.'),
    });
  }

  private applyFeaturedCountriesFromDoc(d: Record<string, unknown>): void {
    const entries = (d['featuredCountries'] ?? []) as Array<{ slug?: string; cardMediaAssetId?: string }>;
    const slugs = (d['featuredCountrySlugs'] ?? []) as string[];
    const flatMap = (d['featuredCountryCardMedia'] ?? {}) as Record<string, unknown>;
    const previous = this.form.featuredCountryCardMedia;
    const fromServer: Record<string, string> = {};
    for (const [slug, id] of Object.entries(flatMap)) {
      const mediaId = String(id ?? '').trim();
      if (slug && mediaId) fromServer[slug] = mediaId;
    }
    for (const entry of entries) {
      if (entry.slug && entry.cardMediaAssetId) fromServer[entry.slug] = String(entry.cardMediaAssetId);
    }
    const resolvedSlugs =
      slugs.length > 0 ? [...slugs] : entries.map((e) => String(e.slug ?? '')).filter(Boolean);
    const cardMedia: Record<string, string> = {};
    for (const slug of resolvedSlugs) {
      const mediaId = fromServer[slug] ?? previous[slug];
      if (mediaId) cardMedia[slug] = mediaId;
    }
    this.form.featuredCountrySlugs = resolvedSlugs;
    this.form.featuredCountryCardMedia = cardMedia;
    this.loadCountryCardPreviews(this.countryCardPreviewUrls());
  }


  private loadCountryCardPreviews(existing: Record<string, string> = {}): void {
    const media = this.form.featuredCountryCardMedia;
    const slugs = Object.keys(media);
    if (slugs.length === 0) {
      this.countryCardPreviewUrls.set({});
      return;
    }
    const previews: Record<string, string> = { ...existing };
    let pending = slugs.length;
    for (const slug of slugs) {
      const id = media[slug]?.trim();
      if (!id) {
        pending -= 1;
        if (pending === 0) this.countryCardPreviewUrls.set({ ...previews });
        continue;
      }
      if (previews[slug]) {
        pending -= 1;
        if (pending === 0) this.countryCardPreviewUrls.set({ ...previews });
        continue;
      }
      this.media.getById(id).subscribe({
        next: (asset) => {
          previews[slug] = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
          pending -= 1;
          if (pending === 0) this.countryCardPreviewUrls.set({ ...previews });
        },
        error: () => {
          pending -= 1;
          if (pending === 0) this.countryCardPreviewUrls.set({ ...previews });
        },
      });
    }
  }

  private docToForm(d: Record<string, unknown>): HomeMarketingForm {
    const ann = (d['announcement'] ?? {}) as Record<string, unknown>;
    const popup = (d['popup'] ?? {}) as Record<string, unknown>;
    const social = (d['socialLinks'] ?? {}) as Record<string, unknown>;
    const seo = (d['seo'] ?? {}) as Record<string, unknown>;
    const cta = (d['cta'] ?? {}) as Record<string, unknown>;
    const stats = (d['stats'] ?? []) as Array<{ value?: string; label?: string; icon?: string }>;
    const statsText = stats
      .map((s) => [s.value ?? '', s.label ?? '', s.icon ?? ''].filter((x) => x !== '').join('|'))
      .join('\n');
    const heroFeatures = (d['heroFeatures'] ?? []) as string[];
    const featuredEntries = (d['featuredCountries'] ?? []) as Array<{ slug?: string; cardMediaAssetId?: string }>;
    const featuredSlugs = (d['featuredCountrySlugs'] ?? []) as string[];
    const featuredFlatMap = (d['featuredCountryCardMedia'] ?? {}) as Record<string, unknown>;
    const featuredCountryCardMedia: Record<string, string> = {};
    for (const [slug, id] of Object.entries(featuredFlatMap)) {
      const mediaId = String(id ?? '').trim();
      if (slug && mediaId) featuredCountryCardMedia[slug] = mediaId;
    }
    for (const entry of featuredEntries) {
      if (entry.slug && entry.cardMediaAssetId) {
        featuredCountryCardMedia[entry.slug] = String(entry.cardMediaAssetId);
      }
    }
    const featured =
      featuredSlugs.length > 0
        ? featuredSlugs
        : featuredEntries.map((e) => String(e.slug ?? '')).filter(Boolean);
    const tIds = (d['testimonialIds'] ?? []) as unknown[];
    const heroGalleryRaw = (d['heroGallery'] ?? []) as Array<{ mediaAssetId?: string; caption?: string }>;
    let heroGallery = heroGalleryRaw
      .map((entry) => {
        const mediaAssetId = String(entry.mediaAssetId ?? '').trim();
        if (!mediaAssetId) return null;
        return {
          key: this.newHeroGalleryKey(),
          mediaAssetId,
          caption: String(entry.caption ?? ''),
        };
      })
      .filter((entry): entry is HeroGalleryFormEntry => entry !== null);
    const legacyArtId = String(d['heroArtMediaAssetId'] ?? '').trim();
    if (heroGallery.length === 0 && legacyArtId) {
      heroGallery = [{ key: this.newHeroGalleryKey(), mediaAssetId: legacyArtId, caption: '' }];
    }
    const coverFromDoc = String(d['heroGalleryCoverMediaAssetId'] ?? '').trim();
    const coverMediaAssetId = coverFromDoc || heroGallery[0]?.mediaAssetId || legacyArtId || '';
    heroGallery = this.heroGalleryWithCoverFirst(heroGallery, coverMediaAssetId);
    const heroGalleryCoverMediaAssetId = heroGallery[0]?.mediaAssetId ?? coverMediaAssetId;
    return {
      announcementPill: String(ann['pill'] ?? ''),
      announcementText: String(ann['text'] ?? ''),
      announcementCtaLabel: String(ann['ctaLabel'] ?? ''),
      announcementCtaHref: String(ann['ctaHref'] ?? ''),
      popupEnabled: Boolean(popup['enabled']),
      popupDelaySeconds: Number(popup['delaySeconds'] ?? 5) || 5,
      popupImageMediaAssetId: String(popup['imageMediaAssetId'] ?? ''),
      socialInstagram: String(social['instagram'] ?? ''),
      socialFacebook: String(social['facebook'] ?? ''),
      socialYoutube: String(social['youtube'] ?? ''),
      heroBadge: String(d['heroBadge'] ?? ''),
      heroTitle: String(d['heroTitle'] ?? ''),
      heroLede: String(d['heroLede'] ?? ''),
      heroFeaturesText: heroFeatures.join('\n'),
      heroPrimaryCtaText: String(d['heroPrimaryCtaText'] ?? ''),
      heroPrimaryCtaAnchor: String(d['heroPrimaryCtaAnchor'] ?? ''),
      heroMediaAssetId: String(d['heroMediaAssetId'] ?? ''),
      heroArtMediaAssetId: String(d['heroArtMediaAssetId'] ?? ''),
      heroGallery,
      heroGalleryCoverMediaAssetId,
      heroSideBadgeValue: String(d['heroSideBadgeValue'] ?? ''),
      heroSideBadgeLabel: String(d['heroSideBadgeLabel'] ?? ''),
      statsText,
      featuredCountrySlugs: [...featured],
      featuredCountryCardMedia,
      destinationsEyebrow: String(d['destinationsEyebrow'] ?? ''),
      destinationsTitle: String(d['destinationsTitle'] ?? ''),
      destinationsSubtitle: String(d['destinationsSubtitle'] ?? ''),
      whyEyebrow: String(d['whyEyebrow'] ?? ''),
      whyTitle: String(d['whyTitle'] ?? ''),
      whySubtitle: String(d['whySubtitle'] ?? ''),
      whyFeaturesJson: JSON.stringify(d['whyFeatures'] ?? [], null, 2),
      testimonialIdsCsv: tIds.map((x) => String(x)).join(', '),
      testimonialsTitle: String(d['testimonialsTitle'] ?? ''),
      testimonialsSubtitle: String(d['testimonialsSubtitle'] ?? ''),
      partnersTitle: String(d['partnersTitle'] ?? ''),
      partnersJson: JSON.stringify(d['partners'] ?? [], null, 2),
      ctaEyebrow: String(cta['eyebrow'] ?? ''),
      ctaTitle: String(cta['title'] ?? ''),
      ctaSubtitle: String(cta['subtitle'] ?? ''),
      ctaBulletsCsv: ((cta['bullets'] ?? []) as string[]).join(', '),
      ctaFormHeading: String(cta['formHeading'] ?? ''),
      ctaLegalLine: String(cta['legalLine'] ?? ''),
      seoTitle: String(seo['title'] ?? ''),
      seoDescription: String(seo['description'] ?? ''),
      seoKeywordsCsv: ((seo['keywords'] ?? []) as string[]).join(', '),
      seoOgImageMediaAssetId: String(seo['ogImageMediaAssetId'] ?? ''),
    };
  }

  onHeroBackgroundFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.heroBgFile = input.files?.[0] ?? null;
    this.heroBgUploadMessage.set(null);
  }

  uploadHeroBackground(): void {
    if (!this.heroBgFile) return;
    this.heroBgUploading.set(true);
    this.heroBgUploadMessage.set(null);
    this.media.upload(this.heroBgFile, { altText: 'Home hero background' }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          this.heroBgUploading.set(false);
          this.heroBgUploadMessage.set('Upload succeeded but no asset id was returned.');
          return;
        }
        this.form.heroMediaAssetId = assetId;
        this.heroBgPreviewUrl.set(asset.url);
        this.heroBgFile = null;
        this.persistHeroMediaField('heroMediaAssetId', assetId, () => {
          this.heroBgUploading.set(false);
          this.heroBgUploadMessage.set('Background saved. It will appear on the public homepage.');
        }, (msg) => {
          this.heroBgUploading.set(false);
          this.heroBgUploadMessage.set(`${msg} Re-upload or click Save to retry.`);
        });
      },
      error: (err) => {
        this.heroBgUploading.set(false);
        this.heroBgUploadMessage.set(err?.error?.message ?? 'Upload failed.');
      },
    });
  }

  clearHeroBackground(): void {
    this.form.heroMediaAssetId = '';
    this.heroBgPreviewUrl.set(null);
    this.heroBgFile = null;
    this.persistHeroMediaField('heroMediaAssetId', '', () => {
      this.heroBgUploadMessage.set('Background removed from the public homepage.');
    }, (msg) => {
      this.heroBgUploadMessage.set(msg);
    });
  }

  onHeroGalleryFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;
    if (!this.canAddHeroGalleryImages()) {
      this.heroGalleryMessage.set(`You can add at most ${this.maxHeroGalleryImages} gallery images.`);
      return;
    }

    const allowed = files.slice(0, this.maxHeroGalleryImages - this.form.heroGallery.length);
    this.heroGalleryUploading.set(true);
    this.heroGalleryMessage.set(null);
    this.uploadHeroGalleryFiles(allowed, [], () => {
      this.heroGalleryUploading.set(false);
      this.heroGalleryMessage.set('Gallery images saved. They will appear on the public homepage.');
    }, (msg) => {
      this.heroGalleryUploading.set(false);
      this.heroGalleryMessage.set(msg);
    });
  }

  canAddHeroGalleryImages(): boolean {
    return this.form.heroGallery.length < this.maxHeroGalleryImages && !this.heroGalleryUploading();
  }

  removeHeroGalleryItem(key: string): void {
    this.form.heroGallery = this.form.heroGallery.filter((item) => item.key !== key);
    this.syncHeroGalleryCover();
    this.heroGalleryPreviewUrls.update((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    this.persistHeroGallery(
      () => this.heroGalleryMessage.set('Gallery image removed.'),
      (msg) => this.heroGalleryMessage.set(msg),
    );
  }

  onHeroGalleryDrop(event: CdkDragDrop<HeroGalleryFormEntry[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.form.heroGallery, event.previousIndex, event.currentIndex);
    this.form.heroGallery = [...this.form.heroGallery];
    this.syncHeroGalleryCover();
    this.persistHeroGallery(
      () => this.heroGalleryMessage.set('Gallery order updated.'),
      (msg) => this.heroGalleryMessage.set(msg),
    );
  }

  heroGalleryPreview(key: string): string | null {
    return this.heroGalleryPreviewUrls()[key] ?? null;
  }

  private uploadHeroGalleryFiles(
    files: File[],
    uploaded: Array<{ asset: MediaAssetDto; caption: string }>,
    onSuccess: () => void,
    onError: (message: string) => void,
  ): void {
    const file = files[0];
    if (!file) {
      this.applyUploadedHeroGallery(uploaded, onSuccess, onError);
      return;
    }
    this.media.upload(file, { altText: 'Home hero gallery' }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          onError('Upload succeeded but no asset id was returned.');
          return;
        }
        this.uploadHeroGalleryFiles(files.slice(1), [...uploaded, { asset, caption: '' }], onSuccess, onError);
      },
      error: (err) => onError(err?.error?.message ?? 'Upload failed.'),
    });
  }

  private applyUploadedHeroGallery(
    uploaded: Array<{ asset: MediaAssetDto; caption: string }>,
    onSuccess: () => void,
    onError: (message: string) => void,
  ): void {
    if (!uploaded.length) {
      onSuccess();
      return;
    }
    const newEntries = uploaded.map(({ asset, caption }) => {
      const key = this.newHeroGalleryKey();
      const mediaAssetId = this.mediaAssetId(asset);
      const previewUrl = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
      this.heroGalleryPreviewUrls.update((prev) => ({ ...prev, [key]: previewUrl }));
      return { key, mediaAssetId, caption };
    });
    this.form.heroGallery = [...this.form.heroGallery, ...newEntries].slice(0, this.maxHeroGalleryImages);
    this.syncHeroGalleryCover();
    this.persistHeroGallery(onSuccess, onError);
  }

  private buildHeroGalleryPayload(): Record<string, unknown> {
    const heroGallery = this.form.heroGallery
      .map((item) => {
        const mediaAssetId = item.mediaAssetId.trim();
        if (!mediaAssetId) return null;
        const caption = item.caption.trim();
        return {
          mediaAssetId,
          ...(caption ? { caption } : {}),
        };
      })
      .filter((item): item is { mediaAssetId: string; caption?: string } => item !== null);
    if (!heroGallery.length) {
      return {
        heroGallery: [],
        heroGalleryCoverMediaAssetId: '',
        heroArtMediaAssetId: '',
      };
    }
    const cover = heroGallery[0].mediaAssetId;
    return {
      heroGallery,
      heroGalleryCoverMediaAssetId: cover,
      heroArtMediaAssetId: cover,
    };
  }

  private syncHeroGalleryCover(): void {
    this.form.heroGalleryCoverMediaAssetId = this.form.heroGallery[0]?.mediaAssetId ?? '';
  }

  private heroGalleryWithCoverFirst(
    gallery: HeroGalleryFormEntry[],
    coverMediaAssetId: string,
  ): HeroGalleryFormEntry[] {
    if (!coverMediaAssetId || gallery.length < 2) return gallery;
    const coverIndex = gallery.findIndex((item) => item.mediaAssetId === coverMediaAssetId);
    if (coverIndex <= 0) return gallery;
    const reordered = [...gallery];
    const [coverItem] = reordered.splice(coverIndex, 1);
    reordered.unshift(coverItem);
    return reordered;
  }

  private persistHeroGallery(onSuccess: () => void, onError: (message: string) => void): void {
    this.pages.patchPlatformMarketingHome(this.buildHeroGalleryPayload()).subscribe({
      next: (d) => {
        this.applyHeroGalleryFromDoc(d);
        onSuccess();
      },
      error: (err) => onError(err?.error?.message ?? 'Could not save hero gallery.'),
    });
  }

  private applyHeroGalleryFromDoc(d: Record<string, unknown>): void {
    const parsed = this.docToForm(d);
    this.form.heroGallery = parsed.heroGallery;
    this.form.heroGalleryCoverMediaAssetId = parsed.heroGalleryCoverMediaAssetId;
    this.form.heroArtMediaAssetId = parsed.heroArtMediaAssetId;
    this.loadHeroGalleryPreviews();
  }

  private newHeroGalleryKey(): string {
    this.heroGalleryKeySeq += 1;
    return `hg-${this.heroGalleryKeySeq}`;
  }

  private loadHeroGalleryPreviews(existing: Record<string, string> = {}): void {
    const items = this.form.heroGallery;
    if (!items.length) {
      this.heroGalleryPreviewUrls.set({});
      return;
    }
    const previews: Record<string, string> = { ...existing };
    let pending = items.length;
    for (const item of items) {
      if (previews[item.key]) {
        pending -= 1;
        if (pending === 0) this.heroGalleryPreviewUrls.set({ ...previews });
        continue;
      }
      const id = item.mediaAssetId.trim();
      if (!id) {
        pending -= 1;
        if (pending === 0) this.heroGalleryPreviewUrls.set({ ...previews });
        continue;
      }
      this.media.getById(id).subscribe({
        next: (asset) => {
          previews[item.key] = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
          pending -= 1;
          if (pending === 0) this.heroGalleryPreviewUrls.set({ ...previews });
        },
        error: () => {
          pending -= 1;
          if (pending === 0) this.heroGalleryPreviewUrls.set({ ...previews });
        },
      });
    }
  }

  onPopupFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.popupFile = input.files?.[0] ?? null;
    this.popupUploadMessage.set(null);
  }

  uploadPopupImage(): void {
    if (!this.popupFile) return;
    this.popupUploading.set(true);
    this.popupUploadMessage.set(null);
    this.media.upload(this.popupFile, { altText: 'Home page popup' }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          this.popupUploading.set(false);
          this.popupUploadMessage.set('Upload succeeded but no asset id was returned.');
          return;
        }
        this.form.popupImageMediaAssetId = assetId;
        const previewUrl = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
        this.popupPreviewUrl.set(previewUrl);
        this.popupFile = null;
        this.persistPopupImage(assetId, () => {
          this.popupUploading.set(false);
          this.popupUploadMessage.set('Popup image saved.');
        }, (msg) => {
          this.popupUploading.set(false);
          this.popupUploadMessage.set(msg);
        });
      },
      error: (err) => {
        this.popupUploading.set(false);
        this.popupUploadMessage.set(err?.error?.message ?? 'Upload failed.');
      },
    });
  }

  clearPopupImage(): void {
    this.form.popupImageMediaAssetId = '';
    this.popupPreviewUrl.set(null);
    this.popupFile = null;
    this.persistPopupImage('', () => {
      this.popupUploadMessage.set('Popup image removed.');
    }, (msg) => {
      this.popupUploadMessage.set(msg);
    });
  }

  private persistPopupImage(
    assetId: string,
    onSuccess: () => void,
    onError: (message: string) => void,
  ): void {
    const body: Record<string, unknown> = {
      popup: {
        enabled: this.form.popupEnabled,
        delaySeconds: this.form.popupDelaySeconds,
        ...(assetId ? { imageMediaAssetId: assetId } : {}),
      },
    };
    this.pages.patchPlatformMarketingHome(body).subscribe({
      next: (d) => {
        const popup = (d['popup'] ?? {}) as Record<string, unknown>;
        this.form.popupImageMediaAssetId = String(popup['imageMediaAssetId'] ?? assetId);
        onSuccess();
      },
      error: (err) => onError(err?.error?.message ?? 'Could not save popup image.'),
    });
  }

  private mediaAssetId(asset: MediaAssetDto): string {
    const raw = asset._id ?? (asset as MediaAssetDto & { id?: string }).id;
    return raw != null ? String(raw) : '';
  }

  private persistHeroMediaField(
    field: 'heroMediaAssetId',
    assetId: string,
    onSuccess: () => void,
    onError: (message: string) => void,
  ): void {
    const body: Record<string, unknown> = assetId ? { [field]: assetId } : { [field]: '' };
    this.pages.patchPlatformMarketingHome(body).subscribe({
      next: (d) => {
        const saved = String(d[field] ?? assetId);
        this.form[field] = saved;
        onSuccess();
      },
      error: (err) => onError(err?.error?.message ?? 'Could not save image reference.'),
    });
  }

  private loadHeroBackgroundPreview(assetId: string): void {
    this.loadMediaPreview(assetId, (url) => this.heroBgPreviewUrl.set(url));
  }

  private loadPopupPreview(assetId: string): void {
    this.loadMediaPreview(assetId, (url) => this.popupPreviewUrl.set(url));
  }

  private loadMediaPreview(assetId: string, setUrl: (url: string | null) => void): void {
    const id = assetId.trim();
    if (!id) {
      setUrl(null);
      return;
    }
    this.media.getById(id).subscribe({
      next: (asset) => setUrl(asset.url),
      error: () => setUrl(null),
    });
  }

  private parseStats(text: string): Array<{ value: string; label: string; icon?: string }> {
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split('|').map((s) => s.trim());
        const value = parts[0] ?? '';
        const label = parts[1] ?? '';
        const icon = parts[2];
        if (!value) return null;
        return { value, label, ...(icon ? { icon } : {}) };
      })
      .filter((x): x is { value: string; label: string; icon?: string } => x !== null);
  }

  save(): void {
    let whyFeatures: unknown;
    let partners: unknown;
    try {
      whyFeatures = JSON.parse(this.form.whyFeaturesJson || '[]');
      partners = JSON.parse(this.form.partnersJson || '[]');
    } catch {
      this.error.set('Invalid JSON in Why features or Partners.');
      return;
    }

    const testimonialIds = this.form.testimonialIdsCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const body: Record<string, unknown> = {
      announcement: {
        pill: this.form.announcementPill || undefined,
        text: this.form.announcementText || undefined,
        ctaLabel: this.form.announcementCtaLabel || undefined,
        ctaHref: this.form.announcementCtaHref || undefined,
      },
      popup: {
        enabled: this.form.popupEnabled,
        delaySeconds: this.form.popupDelaySeconds,
        ...(this.form.popupImageMediaAssetId ? { imageMediaAssetId: this.form.popupImageMediaAssetId } : {}),
      },
      socialLinks: {
        instagram: this.form.socialInstagram || undefined,
        facebook: this.form.socialFacebook || undefined,
        youtube: this.form.socialYoutube || undefined,
      },
      heroBadge: this.form.heroBadge || undefined,
      heroTitle: this.form.heroTitle || undefined,
      heroLede: this.form.heroLede || undefined,
      heroFeatures: this.form.heroFeaturesText.split('\n').map((s) => s.trim()).filter(Boolean),
      heroPrimaryCtaText: this.form.heroPrimaryCtaText || undefined,
      heroPrimaryCtaAnchor: this.form.heroPrimaryCtaAnchor || undefined,
      heroMediaAssetId: this.form.heroMediaAssetId || undefined,
      ...this.buildHeroGalleryPayload(),
      heroSideBadgeValue: this.form.heroSideBadgeValue || undefined,
      heroSideBadgeLabel: this.form.heroSideBadgeLabel || undefined,
      stats: this.parseStats(this.form.statsText),
      ...this.buildFeaturedCountriesPayload(),
      destinationsEyebrow: this.form.destinationsEyebrow || undefined,
      destinationsTitle: this.form.destinationsTitle || undefined,
      destinationsSubtitle: this.form.destinationsSubtitle || undefined,
      whyEyebrow: this.form.whyEyebrow || undefined,
      whyTitle: this.form.whyTitle || undefined,
      whySubtitle: this.form.whySubtitle || undefined,
      whyFeatures,
      testimonialIds,
      testimonialsTitle: this.form.testimonialsTitle || undefined,
      testimonialsSubtitle: this.form.testimonialsSubtitle || undefined,
      partnersTitle: this.form.partnersTitle || undefined,
      partners,
      cta: {
        eyebrow: this.form.ctaEyebrow || undefined,
        title: this.form.ctaTitle || undefined,
        subtitle: this.form.ctaSubtitle || undefined,
        bullets: this.form.ctaBulletsCsv.split(',').map((s) => s.trim()).filter(Boolean),
        formHeading: this.form.ctaFormHeading || undefined,
        legalLine: this.form.ctaLegalLine || undefined,
      },
      seo: {
        title: this.form.seoTitle || '',
        description: this.form.seoDescription || '',
        keywords: this.form.seoKeywordsCsv.split(',').map((s) => s.trim()).filter(Boolean),
        ogImageMediaAssetId: this.form.seoOgImageMediaAssetId || undefined,
      },
    };

    this.saving.set(true);
    this.error.set(null);
    this.pages.patchPlatformMarketingHome(body).subscribe({
      next: (d) => {
        this.form = this.docToForm(d);
        this.loadHeroBackgroundPreview(this.form.heroMediaAssetId);
        this.loadHeroGalleryPreviews();
        this.loadPopupPreview(this.form.popupImageMediaAssetId);
        this.applyFeaturedCountriesFromDoc(d);
        this.saving.set(false);
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message ?? 'Save failed.');
      },
    });
  }
}
